"use client";

import React, { useState, useEffect } from "react";

import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Lock, Info, ArrowLeft, ChevronDown, Clock3, X } from "lucide-react";
import EnableTwoFA from "./EnableTwoFA";
import { useAuth } from "../context/AuthContext";
import TwoFA from "./MainTwoFA";
import withOverlay from "../hoc/withOverlay";
import CurrencyFormatter from "../utils/currencyFormatter";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import { FormattedDate } from "../utils/dateFormatter";

const TwoFAOverlay = withOverlay(TwoFA);

const SendConfirmationForex = ({
  asset,
  zar,
  currency,
  destinationAddress,
  onCancel,
  currentRate,
  disableBtn,
  onConfirm,
  setUp,
  quoteData,
}) => {
  const [open, setOpen] = useState(false);
  const [isTwoFAOpen, setIsTwoFAOpen] = useState(false);
  const [openSeeMore, setOpenSeeMore] = useState(false);
  const [openDate, setOpenDate] = useState(false);
  const [date, setDate] = useState("");
  const [isEnabled, setIsEnabled] = useState(false);
  const { settings } = useAuth();

  useEffect(() => {
    setIsEnabled(settings?.security?.twoFactorAuth?.enabled);
  }, [settings]);

  const handleLocalBack = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleTwoFA = () => {
    if (!isEnabled) {
      setOpen(true);
    }
    if (isEnabled) {
      onConfirm();
    }
  };
  const handleSetUp = () => {
    if (setUp) {
      setUp();
    }
  };
  const toggleAccordionSeeMore = () => setOpenSeeMore((prev) => !prev);
  const schedulePayment = () => {
    setOpenDate(true);
  };
  const formatter = new CurrencyFormatter();
  const dateFormat = new FormattedDate();
  const detailedDate = dateFormat.simpleFormat(date);
  return (
    <Card className="w-full min-w-[42rem] max-w-4xl p-8 bg-white !shadow-none rounded-2xl relative">
      <div className="text-center px-6 pt-8">
        <h2 className="text-3xl font-bold text-gray-800">
          {quoteData?.amountReceivedFormatted}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          at {asset?.asset?.toUpperCase()}/{currency?.toUpperCase()}{" "}
          {currentRate > 0
            ? currentRate.toLocaleString("en-ZA", { maximumFractionDigits: 2 })
            : "Loading..."}
        </p>
      </div>

      {/* Transaction Details */}
      <CardContent className="px-6 mt-6 w-full">
        <h3 className="text-base font-semibold text-gray-700 mb-3">
          Transaction details
        </h3>
        <div className="border rounded-lg divide-y">
          <div className="p-4 flex justify-between">
            <span className="text-sm text-gray-700">Send to</span>
            <span className="text-sm font-medium text-gray-800">
              <p className="text-gray-800 font-semibold text-right">
                {destinationAddress?.name}
              </p>
            </span>
          </div>
          <div className="p-4 flex justify-between">
            <span className="text-sm text-gray-700">You Send</span>
            <span className="text-sm font-bold text-gray-800">
              {formatter.format(zar, "ZAR")}
            </span>
          </div>

          <div className="p-4 flex justify-between items-center">
            <span className="flex items-center gap-1 text-sm text-gray-700">
              Beneficiary gets
            </span>
            <span className="text-sm font-medium text-gray-800">
              {quoteData?.amountReceivedFormatted}
            </span>
          </div>
          <div className="p-4 flex justify-between items-center">
            <span className="flex items-center gap-1 text-sm text-gray-700">
              Service charge
              <Info size={14} className="text-gray-500" />
            </span>
            <span className="text-sm font-medium text-gray-800">
              {quoteData?.withdrawalFeeFormatted ?? "—"}
            </span>
          </div>
          <div className="p-4 flex justify-between">
            <span className="text-sm text-gray-700">Total cost</span>
            <span className="text-sm font-bold text-gray-900">
              {quoteData?.totalAmountFormatted}
            </span>
          </div>
        </div>
        <h3 className="text-base font-semibold text-gray-700 mb-3 mt-7">
          Beneficiary details
        </h3>
        <div className="border rounded-lg divide-y">
          <div className="p-4 flex justify-between">
            <span className="text-sm text-gray-700">Name</span>
            <span className="text-sm font-medium text-gray-800">
              <p className="text-gray-800 font-semibold text-right">
                {destinationAddress?.name}
              </p>
            </span>
          </div>
          <div className="p-4 flex justify-between">
            <span className="text-sm text-gray-700">Bank</span>
            <span className="text-sm font-bold text-gray-800">
              {destinationAddress?.bank}
            </span>
          </div>

          <div className="p-4 flex justify-between items-center">
            <span className="flex items-center gap-1 text-sm text-gray-700">
              Account Number
            </span>
            <span className="text-sm font-medium text-gray-800">
              {destinationAddress?.accountNumber}
            </span>
          </div>
          {openSeeMore && (
            <>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Branch Code</span>
                <span className="text-sm font-medium text-gray-800">
                  {destinationAddress?.branchCode}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Account Type</span>
                <span className="text-sm font-medium text-gray-800">
                  {destinationAddress?.accountType}
                </span>
              </div>
              <div className="p-4 flex justify-between">
                <span className="text-sm text-gray-700">Status</span>
                <span className="text-sm font-medium text-gray-800">
                  {destinationAddress?.status}
                </span>
              </div>
            </>
          )}

          <div className="p-4">
            <div
              onClick={toggleAccordionSeeMore}
              className="flex justify-between cursor-pointer items-center"
            >
              <span className="text-sm text-gray-700">
                {openSeeMore ? "See Less" : "See More"}
              </span>
              <span
                className="text-sm font-medium text-gray-600 transition-transform"
                style={{
                  transform: openSeeMore ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                <ChevronDown />
              </span>
            </div>
          </div>
        </div>
      </CardContent>

      <div className="relative w-full px-6 py-6 flex items-center">
        {/* Left: Arrow Button */}
        <div className="absolute left-6">
          <Button
            variant="ghost"
            onClick={handleLocalBack}
            className="w-10 h-10 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
          >
            <ArrowLeft size={20} className="text-gray-700" />
          </Button>
        </div>

        <div className="mx-auto flex items-center gap-4">
          <Dialog open={openDate} onOpenChange={setOpenDate}>
            <DialogTrigger asChild>
              <Button
                variant="ghost"
                onClick={schedulePayment}
                className="w-52 h-10 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
              >
                {date ? (
                  <div className="flex items-center justify-around w-full px-3">
                    <span>{detailedDate}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevents triggering schedulePayment
                        setDate(null);
                      }}
                      className="text-red-500 font-bold text-sm ml-2 hover:underline"
                    >
                      Undo
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Clock3 size={20} className="text-gray-700 mr-2" />
                    Schedule this payment
                  </div>
                )}
              </Button>
            </DialogTrigger>

            <DialogContent className="max-w-2xl rounded-2xl p-6">
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  onClick={() => setOpen(false)}
                  className="w-10 h-10 absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
                >
                  <X size={18} className="text-gray-700" />
                </Button>
              </DialogClose>

              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-gray-800">
                  Schedule Payment
                </DialogTitle>
              </DialogHeader>
              <div className="flex items-center justify-center mt-4">
                <div className="text-center px-6 pt-8">
                  <h2 className="text-xl font-bold text-gray-800">
                    {quoteData?.amountReceivedFormatted}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    at {asset?.asset?.toUpperCase()}/{currency?.toUpperCase()}{" "}
                    {currentRate > 0
                      ? currentRate.toLocaleString("en-ZA", {
                          maximumFractionDigits: 2,
                        })
                      : "Loading..."}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-center justify-between h-72 space-y-4 px-4 py-2">
                <div className="flex-grow flex items-center justify-center">
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-2 text-gray-700 shadow-sm w-60 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <Button
                  onClick={() => setOpenDate(false)}
                  className={`w-full py-4 px-8 text-sm font-bold text-white rounded-full ${
                    disableBtn
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-tr from-purple-500 to-rose-500 hover:opacity-90"
                  }`}
                  disabled={disableBtn}
                >
                  Confirm
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button
            onClick={handleTwoFA}
            className={`py-5 px-8 text-sm font-bold text-white rounded-full ${
              disableBtn
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-tr from-purple-500 to-rose-500 hover:opacity-90"
            }`}
            disabled={disableBtn}
          >
            Confirm
          </Button>

          <EnableTwoFA
            open={open}
            setOpen={setOpen}
            setIsTwoFAOpen={setIsTwoFAOpen}
          />
        </div>
      </div>

      <div className="flex items-center justify-center gap-1 text-sm text-gray-500 pb-6">
        <Lock size={12} />
        <p>Secured by 80Eight</p>
      </div>

      {/* Shows TwoFA overlay */}
      <TwoFAOverlay
        isOpen={isTwoFAOpen}
        closeOverlay={() => setIsTwoFAOpen(false)}
        isSetUp={handleSetUp}
      />
    </Card>
  );
};

export default SendConfirmationForex;
